import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { useCreateTicket } from '../../hooks/use-tickets';
import { useClients, useClientContracts } from '../../hooks/use-clients';
import { useAuthStore } from '../../stores/auth-store';
import { ALL_PRIORITIES, PRIORITY_LABELS, PRIORITY_DESCRIPTIONS } from '../../lib/constants';
import { api } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';

// Must match backend ALLOWED_MIMES (non-client roles).
const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.heic', '.heif', '.gif', '.svg', '.webp', '.pdf', '.docx', '.xlsx', '.pptx', '.txt', '.zip'];

const schema = z.object({
  subject: z.string().min(1, 'Subiectul este obligatoriu'),
  description: z.string().min(1, 'Descrierea este obligatorie'),
  priority: z.enum(['ESTETIC', 'NORMAL', 'CRITIC', 'BUG', 'CHANGE_REQUEST']),
  clientElementId: z.string().min(1, 'Selectează clientul'),
  projectId: z.string().min(1, 'Contractul este obligatoriu'),
  subcategoryId: z.string().min(1, 'Proiectul / subcategoria este obligatorie'),
});

type FormData = z.infer<typeof schema>;

export function AdminNewTicketPage() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const createTicket = useCreateTicket();
  const { toast } = useToast();

  // Load WebCon clients (companies)
  const { data: clientsData, isLoading: clientsLoading } = useClients({ pageSize: 500 });
  const clients = clientsData?.data ?? [];

  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { priority: 'NORMAL', clientElementId: '', projectId: '', subcategoryId: '' },
  });

  const selectedClientId = watch('clientElementId');
  const selectedContractId = watch('projectId');

  // Load contracts for the selected client
  const { data: contractsData, isLoading: contractsLoading } = useClientContracts(selectedClientId);
  const contracts = contractsData?.data ?? [];

  // Get subcategories from the selected contract
  const subcategories = useMemo(() => {
    if (!selectedContractId) return [];
    const contract = contracts.find((c) => String(c.elementId) === selectedContractId);
    return contract?.subcategories ?? [];
  }, [contracts, selectedContractId]);

  const onSubmit = async (data: FormData) => {
    if (isSubmitting) return;
    const invalid = files.filter((f) => !ALLOWED_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext)));
    if (invalid.length > 0) {
      toast('error', `Tip de fișier nepermis: ${invalid.map((f) => f.name).join(', ')}. Permise: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }
    setIsSubmitting(true);
    let ticketId: string;
    try {
      const result = await createTicket.mutateAsync({
        subject: data.subject,
        description: data.description,
        priority: data.priority,
        projectId: data.projectId,
        subcategoryId: data.subcategoryId,
        clientElementId: Number(data.clientElementId),
      });
      ticketId = result.data.id;
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Tichetul nu a putut fi creat.');
      setIsSubmitting(false);
      return;
    }

    // Ticket exists from here on: never return to the form, or a retry would create a duplicate.
    const failed: string[] = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append('ticketId', ticketId);
      fd.append('file', file);
      try {
        await api.upload('/attachments', fd);
      } catch (err) {
        failed.push(`${file.name} (${err instanceof Error ? err.message : 'eroare'})`);
      }
    }

    if (failed.length > 0) {
      toast('error', `Tichetul a fost creat, dar unele atașamente nu au putut fi încărcate: ${failed.join('; ')}. Le puteți adăuga din pagina tichetului.`);
      navigate(`/admin/tickets/${ticketId}`);
    } else {
      toast('success', 'Tichetul a fost creat.');
      navigate('/admin/tickets');
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-6 text-2xl font-bold text-gray-900 dark:text-dark-text">Tichet Nou (în numele clientului)</h1>
      <Card>
        <CardHeader><h2 className="font-semibold">Detalii tichet</h2></CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Agent name (read-only) */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text">Utilizator</label>
              <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-dark-border dark:bg-dark-border dark:text-dark-text">
                {currentUser?.name ?? '—'}
              </p>
            </div>

            {/* Client dropdown */}
            <Select
              {...register('clientElementId', {
                onChange: () => { setValue('projectId', ''); setValue('subcategoryId', ''); },
              })}
              label="Client *"
              error={errors.clientElementId?.message}
              options={[
                { value: '', label: clientsLoading ? 'Se încarcă...' : 'Selectează clientul' },
                ...clients.map((c) => ({ value: String(c.elementId), label: c.name })),
              ]}
            />

            <Input {...register('subject')} label="Subiect" error={errors.subject?.message} />
            <Textarea {...register('description')} label="Descriere" rows={6} error={errors.description?.message} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Select
                  {...register('priority')}
                  label="Prioritate"
                  options={ALL_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))}
                />
                {watch('priority') && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-dark-muted italic">{PRIORITY_DESCRIPTIONS[watch('priority')]}</p>
                )}
              </div>
              <Select
                {...register('projectId', {
                  onChange: () => { setValue('subcategoryId', ''); },
                })}
                label="Contract *"
                error={errors.projectId?.message}
                disabled={!selectedClientId}
                options={[
                  { value: '', label: !selectedClientId ? 'Selectează mai întâi clientul' : contractsLoading ? 'Se încarcă...' : 'Selectează contractul' },
                  ...contracts.map((c) => ({ value: String(c.elementId), label: c.name })),
                ]}
              />
            </div>
            <Select
              {...register('subcategoryId')}
              label="Proiect (subcategorie) *"
              error={errors.subcategoryId?.message}
              disabled={!selectedContractId}
              options={[
                { value: '', label: selectedContractId ? 'Selectează proiectul' : 'Selectează mai întâi contractul' },
                ...subcategories.map((s) => ({ value: String(s.elementId), label: s.name })),
              ]}
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text">Atașamente</label>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-card dark:text-dark-text dark:hover:bg-dark-border">
                Alege fișiere
                <input
                  type="file"
                  multiple
                  accept={ALLOWED_EXTENSIONS.join(',')}
                  onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])])}
                  className="hidden"
                />
              </label>
              {files.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {files.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs dark:bg-dark-border dark:text-dark-text">
                      {f.name}
                      <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 dark:text-dark-muted">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => navigate('/admin/tickets')} disabled={isSubmitting}>Anulează</Button>
              <Button type="submit" disabled={isSubmitting || createTicket.isPending}>
                {isSubmitting ? 'Se creează...' : 'Creează tichet'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
